import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";

/**
 * Standardized page header used on every list/detail page.
 *
 * Props:
 *   title       string — H1, uses .text-page-title
 *   subtitle    string — one-liner describing the page, uses .text-body-muted
 *   breadcrumbs [{ label, to }] — optional; renders above the title
 *   actions     ReactNode — right-side primary action buttons
 *   testid      string — data-testid for the whole header
 */
export default function PageHeader({ title, subtitle, breadcrumbs, actions, testid }) {
    return (
        <header className="mb-4" data-testid={testid || "page-header"}>
            {breadcrumbs && breadcrumbs.length > 0 && (
                <nav className="flex items-center gap-1 mb-2 text-xs" data-testid="page-header-crumbs" style={{ color: "var(--asl-text-3)" }}>
                    <Home className="h-3 w-3 mr-1" />
                    {breadcrumbs.map((b, i) => (
                        <span key={i} className="flex items-center gap-1">
                            {b.to ? (
                                <Link to={b.to} className="hover:underline transition" style={{ color: "var(--asl-text-2)" }}>{b.label}</Link>
                            ) : (
                                <span style={{ color: "var(--asl-text)" }}>{b.label}</span>
                            )}
                            {i < breadcrumbs.length - 1 && <ChevronRight className="h-3 w-3 opacity-50" />}
                        </span>
                    ))}
                </nav>
            )}
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <h1 className="text-page-title" data-testid="page-header-title">{title}</h1>
                    {subtitle && (
                        <p className="text-body-muted mt-1" data-testid="page-header-subtitle">{subtitle}</p>
                    )}
                </div>
                {actions && (
                    <div className="flex items-center gap-2 flex-shrink-0" data-testid="page-header-actions">
                        {actions}
                    </div>
                )}
            </div>
        </header>
    );
}
